import { useState, useEffect } from 'react';
import { ShoppingBag, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import api from '@/services/api';
import { formatCurrency } from '@/utils';
import { Button } from '@/components/ui/button';
import { OrderStatusBadge } from '@/components/common/OrderStatusBadge';
import toast from '@/hooks/useToast';

interface PickupOrder {
  id: string;
  orderNumber: string;
  pickupToken: string;
  status: string;
  total: number;
  createdAt: string;
  user: { name: string; phone?: string; email: string };
  items: { quantity: number; product: { name: string } }[];
}

const STATUS_FLOW = ['PENDING', 'CONFIRMED', 'PROCESSING', 'DELIVERED'];

export const PickupQueuePage = () => {
  const [orders, setOrders] = useState<PickupOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await api.get('/orders/pickup-queue');
      setOrders(res.data.data);
    } catch { toast.error('Could not load pickup queue'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQueue(); }, []);

  const advanceStatus = async (order: PickupOrder) => {
    const currentIdx = STATUS_FLOW.indexOf(order.status);
    if (currentIdx === -1 || currentIdx >= STATUS_FLOW.length - 1) return;
    const nextStatus = STATUS_FLOW[currentIdx + 1];
    setUpdating(order.id);
    try {
      await api.put(`/orders/${order.id}/status`, {
        status: nextStatus,
        description: nextStatus === 'DELIVERED' ? 'Order collected by customer' : `Order ${nextStatus.toLowerCase()}`,
      });
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: nextStatus } : o));
      if (nextStatus === 'DELIVERED') {
        setOrders(prev => prev.filter(o => o.id !== order.id));
      }
      toast.success(`Token ${order.pickupToken} marked as ${nextStatus.toLowerCase()}`);
    } catch { toast.error('Could not update status'); }
    finally { setUpdating(null); }
  };

  const getNextAction = (status: string) => {
    const map: Record<string, string> = {
      PENDING: 'Confirm Order',
      CONFIRMED: 'Start Preparing',
      PROCESSING: 'Mark Collected',
    };
    return map[status] || null;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
            <ShoppingBag className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-2xl text-gray-800">Today's Pickup Queue</h1>
            <p className="text-sm text-gray-500">{orders.length} order{orders.length !== 1 ? 's' : ''} waiting</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchQueue}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">{Array(3).fill(0).map((_, i) => <div key={i} className="h-32 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle className="h-16 w-16 text-green-300 mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-xl text-gray-700">All clear!</h3>
          <p className="text-gray-400">No pickup orders waiting right now.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => (
            <div key={order.id} className="bg-white rounded-card shadow-card p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{order.pickupToken}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{order.user.name}</p>
                    {order.user.phone && <p className="text-sm text-gray-400">{order.user.phone}</p>}
                  </div>
                </div>
                <div className="text-right">
                  <OrderStatusBadge status={order.status as any} />
                  <p className="text-sm font-bold text-primary-600 mt-1">{formatCurrency(Number(order.total))}</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-3 text-sm">
                {order.items.map((item, i) => (
                  <p key={i} className="text-gray-700">{item.quantity}× {item.product.name}</p>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <Clock className="h-3.5 w-3.5" />
                  <span>#{order.orderNumber}</span>
                </div>
                {getNextAction(order.status) && (
                  <Button
                    size="sm"
                    className="ml-auto"
                    loading={updating === order.id}
                    onClick={() => advanceStatus(order)}
                  >
                    {getNextAction(order.status)}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
